// Status banners + error toast for Phase 4.5 hardening.
// Banners stack at the top of the app shell; the toast is fixed bottom-right.
import { useEffect } from 'react';
import type { ReactNode } from 'react';

type BannerTone = 'error' | 'warn' | 'info';

const TONE_STYLES: Record<BannerTone, { bg: string; color: string; border: string }> = {
  error: { bg: 'var(--err)',  color: '#fff',     border: 'rgba(255,71,87,0.6)' },
  warn:  { bg: 'var(--warn)', color: '#0A0A0F',  border: 'rgba(255,176,32,0.6)' },
  info:  { bg: 'var(--info)', color: '#fff',     border: 'rgba(94,168,255,0.6)' },
};

export function StatusBanner({
  tone, children, onDismiss,
}: {
  tone: BannerTone;
  children: ReactNode;
  onDismiss?: () => void;
}) {
  const { bg, color } = TONE_STYLES[tone];
  return (
    <div role="status" style={{
      padding: '8px 14px', background: bg, color,
      fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      flexShrink: 0,
    }}>
      <span>{children}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: 'transparent', border: 'none', color, cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 14, padding: '0 4px', lineHeight: 1,
          }}
        >✕</button>
      )}
    </div>
  );
}

export function ErrorToast({
  message,
  onDismiss,
  autoDismissMs = 5000,
}: {
  message: string;
  onDismiss: () => void;
  autoDismissMs?: number;
}) {
  useEffect(() => {
    if (autoDismissMs <= 0) return;
    const t = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(t);
  }, [message, autoDismissMs, onDismiss]);

  return (
    <div role="alert" style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
      padding: '12px 16px', borderRadius: 6,
      background: 'var(--err)', color: '#fff',
      fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.04em', lineHeight: 1.4,
      boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'flex-start', gap: 12,
      maxWidth: 420,
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 14, padding: 0, lineHeight: 1,
        }}
      >✕</button>
    </div>
  );
}
